/**
 * Input sanitization utilities
 *
 * Sanitize user input before writing to Firestore.
 * Prevents XSS and ensures data integrity.
 */

/**
 * Strip HTML tags from a string
 * @param {string} input - Raw user input
 * @returns {string} - Sanitized string with HTML removed
 */
export const stripHtml = (input) => {
  if (typeof input !== 'string') return '';
  return input.replace(/<[^>]*>/g, '').trim();
};

/**
 * Sanitize a string field: strip HTML, enforce max length
 * @param {string} input - Raw user input
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} - Sanitized and truncated string
 */
export const sanitizeString = (input, maxLength = 500) => {
  const cleaned = stripHtml(input);
  return cleaned.slice(0, maxLength);
};

/**
 * Sanitize an email address
 * @param {string} email - Raw email input
 * @returns {string} - Lowercased, trimmed email
 */
export const sanitizeEmail = (email) => {
  if (typeof email !== 'string') return '';
  return email.trim().toLowerCase();
};

/**
 * Sanitize a full form data object
 * @param {Object} data - Form data with string values
 * @param {Object} fieldLimits - Map of field name to max length
 * @returns {Object} - Sanitized data
 */
export const sanitizeFormData = (data, fieldLimits = {}) => {
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string') {
      const maxLen = fieldLimits[key] || 500;
      sanitized[key] = sanitizeString(value, maxLen);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};
